import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  CardTitle,
  Badge,
  UncontrolledPopover,
  PopoverBody,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { getAdminDashboard } from "action/admin";
import { adminDashboard } from "constants/admin";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";

const summaryCardConfig = [
  {
    title: "Courses",
    value: 0,
    subtitle: "",
    icon: "ni ni-books",
    theme: "purple",
    path: "/admin/course",
  },
  {
    title: "Batches",
    value: 0,
    subtitle: "",
    icon: "ni ni-calendar-grid-58",
    theme: "green",
    path: "/admin/batch",
  },
  {
    title: "Trainees",
    value: 0,
    subtitle: "Total registered trainees",
    icon: "ni ni-single-02",
    theme: "blue",
    path: "/admin/trainee",
  },
  {
    title: "Instructors",
    value: 0,
    subtitle: "Instructors",
    icon: "ni ni-hat-3",
    theme: "orange",
    path: "/admin/instructor",
  },
  {
    title: "Channel Partners",
    value: 0,
    subtitle: "Registered channel partners",
    icon: "ni ni-building",
    theme: "indigo",
    path: "/admin/channel-partner",
  },
  {
    title: "Certificates Issued",
    value: 0,
    subtitle: "Certificates issued to trainees",
    icon: "ni ni-paper-diploma",
    theme: "teal",
    path: "/admin/certificate/issuance",
  },
];

const actionCardConfig = [
  {
    title: "Certificates Due for Issuance",
    value: 0,
    subtitle: "Eligible trainees",
    icon: "ni ni-paper-diploma",
    theme: "orange",
    path: "/admin/certificate/issuance",
  },
  {
    title: "Letters Expiring Soon",
    value: 0,
    subtitle: "Letters expiring",
    icon: "ni ni-calendar-grid-58",
    theme: "blue",
    path: "/admin/letters",
  },
];

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [pageNum, setPageNum] = useState(1);

  const dashboardResponse = useSelector((state) => state.admin.adminDashboard);

  // Supports both direct response data and a response wrapped in data.
  const dashboardData = dashboardResponse?.data?.summary
    ? dashboardResponse.data
    : dashboardResponse;

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    setListState((prev) => ({ ...prev, isLoading: true }));
    getAdminDashboard(dispatch, pageNum);

    return () => {
      dispatch({
        type: adminDashboard,
        payload: {},
      });
    };
  }, [dispatch, pageNum]);

  // Handle API loading, success, and error states.
  useEffect(() => {
    if (dashboardData?.summary) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: false,
        showLoaderOnClick: false,
      });
    } else if (
      dashboardData?.message ||
      dashboardData?.code >= 400 ||
      dashboardResponse?.message ||
      dashboardResponse?.code >= 400
    ) {
      setListState({
        isLoading: false,
        error:
          dashboardData?.message ||
          dashboardResponse?.message ||
          "Failed to load admin dashboard! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [dashboardData, dashboardResponse]);

  const summary = dashboardData?.summary || {};
  const pendingActionDetails = dashboardData?.pendingActionDetails || {};

  // Dynamic summary cards using API data.
  const summaryCards = summaryCardConfig.map((card) => {
    switch (card.title) {
      case "Courses":
        return {
          ...card,
          value: summary.totalCourses ?? 0,
          subtitle: `${summary.publishedCourses ?? 0} Published · ${
            summary.draftCourses ?? 0
          } Draft`,
        };

      case "Batches":
        return {
          ...card,
          value: summary.totalBatches ?? 0,
          subtitle: `${summary.upcomingBatches ?? 0} Upcoming · ${
            summary.ongoingBatches ?? 0
          } Ongoing · ${summary.completedBatches ?? 0} Completed`,
        };

      case "Trainees":
        return {
          ...card,
          value: summary.totalTrainees ?? 0,
          subtitle: `${summary.totalTrainees ?? 0} Total · ${
            summary.activeTrainees ?? 0
          } Active`,
        };

      case "Instructors":
        return {
          ...card,
          value: summary.totalTrainers ?? 0,
          subtitle: `${summary.totalTrainers ?? 0} Total · ${
            summary.activeTrainers ?? 0
          } Active`,
        };

      case "Channel Partners":
        return {
          ...card,
          value: summary.totalChannelPartners ?? 0,
          subtitle: `${summary.totalChannelPartners ?? 0} Total · ${
            summary.activeChannelPartners ?? 0
          } Active`,
        };

      case "Certificates Issued":
        return {
          ...card,
          value: summary.certificatesIssued ?? 0,
          subtitle: "Certificates issued to trainees",
        };

      default:
        return card;
    }
  });

  // Dynamic values for the existing action cards.
  const actionCards = actionCardConfig.map((card) => {
    switch (card.title) {
      case "Certificates Due for Issuance":
        return {
          ...card,
          value: pendingActionDetails.certificatesDueForIssuance ?? 0,
        };

      case "Letters Expiring Soon":
        return {
          ...card,
          value: pendingActionDetails.lettersExpiringSoon ?? 0,
        };

      default:
        return card;
    }
  });

  // Recently Added Courses API data.
  const recentCourses = (dashboardData?.recentCourses || []).map(
    (course, index) => ({
      ...course,
      srNo: index + 1,
      name: course.courseName || "-",
      category: course.category || "-",
      type: course.courseType || "-",
      createdOn: course.createdAt || "-",
    }),
  );

  // Upcoming Batches API data
  const upcomingBatches = (dashboardData?.upcomingBatches || []).map(
    (batch, index) => ({
      ...batch,
      id: batch.ID || batch.id,
      srNo:
        (pageNum - 1) *
          (dashboardData?.upcomingBatchesNoOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      course: batch.batchName || "-",
      courseName: batch.courseName || "-",

      trainers: batch.trainers?.length
        ? batch.trainers
            .map((trainer) => {
              if (typeof trainer === "string") {
                return trainer;
              }

              // API returns fullName directly
              if (trainer?.fullName) {
                return trainer.fullName;
              }

              // Fallback for APIs returning nested user data
              if (trainer?.user) {
                return `${trainer.user.firstName || ""} ${
                  trainer.user.lastName || ""
                }`.trim();
              }

              return "";
            })
            .filter(Boolean)
        : batch.trainerName
          ? [batch.trainerName]
          : [],

      startDate: batch.startDate,
      trainees: batch.traineeCount ?? 0,
    }),
  );

  const totalBatchPages = Math.ceil(
    (dashboardData?.upcomingBatchesTotalRecords ?? upcomingBatches.length) /
      (dashboardData?.upcomingBatchesNoOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE),
  );

  const courseColumns = useMemo(
    () => [
      {
        Header: "Sr. No.",
        accessor: "srNo",
        minWidth: 70,
        maxWidth: 70,
        centerAlign: true,
      },
      {
        Header: "Course",
        accessor: "name",
        minWidth: 220,
        maxWidth: 220,
        Cell: ({ value }) => <span className="text-wrapper">{value}</span>,
      },
      {
        Header: "Category",
        accessor: "category",
        minWidth: 150,
        maxWidth: 150,
        Cell: ({ value }) => <span className="text-wrapper">{value}</span>,
      },
      {
        Header: "Type",
        accessor: "type",
        centerAlign: true,
        minWidth: 80,
        maxWidth: 80,
        Cell: ({ value }) => (
          <Badge color={value === "LIVE" ? "primary" : "success"} pill>
            {value}
          </Badge>
        ),
      },
      {
        Header: "Created On",
        accessor: "createdOn",
        minWidth: 130,
        maxWidth: 130,
        Cell: ({ value }) => <span className="text-wrapper">{value}</span>,
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        minWidth: 80,
        maxWidth: 80,
        centerAlign: true,
        Cell: ({ row }) => (
          <>
            <Button
              id={`view-certificate-${row.original.id}`}
              color="link"
              className="p-0"
              onClick={() =>
                navigate(
                  `/admin/course/${urlEncodedId(row.original.id)}/details`,
                )
              }
              aria-label="View certificate details"
            >
              <i className="fas fa-eye" />
            </Button>

            <UncontrolledTooltip target={`view-certificate-${row.original.id}`}>
              View Details
            </UncontrolledTooltip>
          </>
        ),
      },
    ],
    [],
  );

  const batchColumns = useMemo(
    () => [
      {
        Header: "Sr. No.",
        accessor: "srNo",
        minWidth: 70,
        maxWidth: 70,
        centerAlign: true,
      },
      {
        Header: "Batch Name",
        accessor: "course",
        minWidth: 170,
        maxWidth: 170,
        Cell: ({ value }) => <span className="text-wrapper">{value}</span>,
      },
      {
        Header: "Course Name",
        accessor: "courseName",
        minWidth: 210,
        maxWidth: 210,
        Cell: ({ value }) => <span className="text-wrapper">{value}</span>,
      },
      {
        Header: "Instructor",
        accessor: "trainers",
        minWidth: 140,
        maxWidth: 140,
        Cell: ({ value, row }) => {
          const trainers = (value || []).filter((trainer) => trainer?.trim());

          if (!trainers.length) {
            return "—";
          }

          const visibleTrainers = trainers.slice(0, 2);
          const remainingCount = trainers.length - 2;
          const trainerPopoverId = `dashboard-trainer-${row.original.id}`;

          return (
            <div className="text-wrapper">
              {visibleTrainers.map((trainer, index) => (
                <div className="trainer-name" key={`${trainer}-${index}`}>
                  <div className="trainer-row">
                    <span className="trainer-bullet">•</span>
                    <span className="trainer-text">{trainer}</span>
                  </div>

                  {index === 1 && remainingCount > 0 && (
                    <div className="trainer-count">
                      <Badge
                        id={trainerPopoverId}
                        color="primary"
                        pill
                        className="cursor-pointer ml-3"
                      >
                        +{remainingCount}
                      </Badge>

                      <UncontrolledPopover
                        target={trainerPopoverId}
                        trigger="hover"
                        placement="top"
                      >
                        <PopoverBody>
                          <div className="trainer-popover-list">
                            {trainers.map((trainerName, trainerIndex) => (
                              <div key={`${trainerName}-${trainerIndex}`}>
                                • {trainerName}
                              </div>
                            ))}
                          </div>
                        </PopoverBody>
                      </UncontrolledPopover>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        minWidth: 120,
        maxWidth: 120,
        Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
      },
      // {
      //   Header: "Trainees",
      //   accessor: "trainees",
      //   minWidth: 90,
      // },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        minWidth: 80,
        maxWidth: 80,
        centerAlign: true,
        Cell: ({ row }) => {
          const batchId = row.original.id;
          const encodedId = urlEncodedId(batchId);
          const tooltipId = `view-dashboard-batch-${batchId}`;

          return (
            <>
              <Button
                id={tooltipId}
                color="link"
                className="p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/batch/${encodedId}/details`);
                }}
                aria-label="View batch details"
              >
                <i className="fas fa-eye table-action-primary" />
              </Button>

              <UncontrolledTooltip target={tooltipId}>
                View Details
              </UncontrolledTooltip>
            </>
          );
        },
      },
    ],
    [],
  );

  const renderSummaryCard = (card) => (
    <Col xl="3" md="6" sm="6" xs="12" key={card.title} className="mb-4">
      <Card
        className={`dashboard-summary-card dashboard-summary-card--${card.theme}`}
        onClick={() => navigate(card.path)}
        role="link"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(card.path);
          }
        }}
      >
        <CardBody>
          <div className="dashboard-summary-card__content">
            <p className="dashboard-summary-card__title">{card.title}</p>

            <h2 className="dashboard-summary-card__value">{card.value}</h2>

            <p className="dashboard-summary-card__subtitle">{card.subtitle}</p>
          </div>

          <div className="dashboard-summary-card__watermark" aria-hidden="true">
            <i className={card.icon} />
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  const renderSectionHeader = (
    title,
    subtitle,
    path,
    buttonText = "View All",
  ) => (
    <CardHeader className="d-flex align-items-center justify-content-between">
      <div>
        <CardTitle tag="h3" className="mb-0 text-default">
          {title}
        </CardTitle>

        {subtitle && <p>{subtitle}</p>}
      </div>

      {path && (
        <Button
          color="link"
          className="dashboard-view-all"
          onClick={() => navigate(path)}
        >
          {buttonText}
          <i className="ni ni-bold-right" />
        </Button>
      )}
    </CardHeader>
  );

  const renderLoader = () => (
    <div className="loaderContainer">
      <i className="fa fa-spinner fa-spin fa-2xl loader" />
    </div>
  );

  const renderError = (message) => (
    <div className="loaderContainer error-style">
      <div className="error-icon">
        <i className="fa-solid fa-circle-exclamation" />
      </div>

      <span>{message}</span>
    </div>
  );

  const renderNoRecords = (message = "No records found.") => (
    <div className="loaderContainer-sm empty-style">
      <div className="empty-icon">
        <i className="fa-solid fa-circle-info" />
      </div>

      <span>{message}</span>
    </div>
  );

  return (
    <Container fluid className="admin-dashboard">
      <Row className="dashboard-summary-grid">
        {summaryCards.slice(0, 4).map(renderSummaryCard)}
      </Row>

      <Row className="dashboard-summary-grid">
        {[...summaryCards.slice(4), ...actionCards].map(renderSummaryCard)}
      </Row>

      <Row>
        <Col>
          <Card>
            {renderSectionHeader(
              "Recently Added Courses",
              null,
              "/admin/course",
            )}

            <CardBody>
              {listState.isLoading ? (
                renderLoader()
              ) : listState.error ? (
                renderError(listState.error)
              ) : recentCourses.length === 0 ? (
                renderNoRecords("No courses found.")
              ) : (
                <div className="table-responsive">
                  <CommonTable
                    columns={courseColumns}
                    data={recentCourses || []}
                    defaultPageSize={10}
                    showPaginationBottom={false}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col className="mb-4">
          <Card>
            {renderSectionHeader("Upcoming Batches", null, "/admin/batch")}

            <CardBody>
              {listState.isLoading ? (
                renderLoader()
              ) : listState.error ? (
                renderError(listState.error)
              ) : upcomingBatches.length === 0 ? (
                renderNoRecords("No upcoming batches found.")
              ) : (
                <div className="table-responsive">
                  <CommonTable
                    columns={batchColumns}
                    data={upcomingBatches || []}
                    defaultPageSize={
                      dashboardData?.upcomingBatchesNoOfRecordsPerPage || 10
                    }
                    showPaginationBottom={totalBatchPages > 1}
                    totalPages={totalBatchPages}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};;

export default AdminDashboard;
